import { prepareAssertions } from '~/packages/general/variable/prepareAssertions.util'
import { Variable } from '~/packages/general/variable/variables.typing'
import { defineAction } from '~/prix'
import { analysis } from '~/prix/types/presets/analysis'
import { assertions } from '~/prix/types/presets/assertions'
import { childrenType, spaceForPoint } from '~/prix/types/presets/space'
import { timeForIncidence } from '~/prix/types/presets/time'
import { variable } from '~/prix/types/presets/variable'
import immunobiologicalStatsAction from './immunobiologicalStats.action.json' with { type: 'json' }
import { z } from 'zod'
import { enumerated } from '~/prix/types/enumerated'
import { dateOrIsoString } from '~/prix/types/date'
import expression from '~/prix/types/expression'
import { getZodSchemaInfo } from '~/prix/types/zodType'
import { select } from '~/prix/types/presets/select'
import { citizenHealthProfileShape } from '../../citizenHealthProfile/stats/citizenHealthProfileStats.definition'
import prefixProperties from '~/prix/utils/prefixProperties'
import { citizenLocationShape, citizenShapeBase } from '../../citizen/stats/citizenShape'
import { healthVariableBaseRelevance } from '~/packages/general/variable/healthVariableRelevance.util'
import { defaultDetectionLayers } from '~/utils/server/stats/defaultTimeSeriesLayers'

const vaccineDoseEnum = enumerated({
  DU: 'Única',
  T1: 'Tratamento com Uma Dose',
  REV: 'Revacinação',
  REF: 'Reforço',
  D0: 'Dose Zero',
  DI: 'Dose Inicial',
  DA: 'Dose Adicional',
  D: 'Dose',
  D5: '5ª Dose',
  D4D: '4ª Dose Dobrada',
  D4: '4ª Dose',
  R3: '3º Reforço',
  D3REV: '3ª Dose Revacinação',
  D3F: '3ª Dose Fracionada',
  D3D: '3ª Dose Dobrada',
  D3: '3ª Dose',
  R2: '2º Reforço',
  D2REVD: '2ª Dose Revacinação Dobrada',
  D2REV: '2ª Dose Revacinação',
  D2F: '2ª Dose Fracionada',
  D2D: '2ª Dose Dobrada',
  D2: '2ª Dose',
  R1: '1º Reforço',
  D1REVD: '1ª Dose Revacinação Dobrada',
  D1REV: '1ª Dose Revacinação',
  D1F: '1ª Dose Fracionada',
  D1D: '1ª Dose Dobrada',
  D1: '1ª Dose',
}).describe('Dose da vacina')

export const immunobiologicalShape = z.object({
  originalId: z.string().describe('ID original'),
  dataSourceId: z.number().describe('ID da fonte de dados'),
  citizenCns: z.string().describe('CNS do cidadão').optional(),
  citizenCpf: z.string().describe('CPF do cidadão').optional(),
  professionalCns: z.string().describe('CNS do profissional'),
  professionalCbo: z.string().describe('CBO do profissional'),
  healthUnitCnes: z.string().describe('CNES da unidade de saúde'),
  teamIne: z.string().describe('INE da equipe de saúde'),
  administrationDateTime: dateOrIsoString().describe('Data e hora da administração da vacina'),
  vaccinationStrategy: z.string().describe('Estratégia de vacinação').optional(),
  immunobiologicalCode: z.string().describe('Código do imunobiológico').optional(),
  immunobiologicalAbbreviation: z.string().describe('Abreviação do imunobiológico').optional(),
  immunobiologicalDescription: z.string().describe('Descrição do imunobiológico').optional(),
  vaccineDoseAbbreviation: vaccineDoseEnum.optional(),
  attendanceGroup: z.string().describe('Grupo de atendimento').optional(),
  vaccineBatch: z.string().describe('Lote da vacina').optional(),
  vaccineManufacturer: z.string().describe('Fabricante da vacina').optional(),
  professionalName: z.string().describe('Nome do profissional de saúde').optional(),
  healthUnitName: z.string().describe('Nome da unidade de saúde').optional(),
  teamName: z.string().describe('Nome da equipe de saúde').optional(),
  vaccineDoseDescription: z.string().describe('Descrição da dose da vacina').optional(),
  ...prefixProperties('citizen.', citizenShapeBase.shape),
  ...prefixProperties('citizen.', citizenLocationShape.shape),
  ...prefixProperties('citizenHealth.', citizenHealthProfileShape.shape),
})

const expressionForCharacteristics = expression(
  immunobiologicalShape.pick({
    immunobiologicalCode: true,
    immunobiologicalAbbreviation: true,
    vaccineDoseAbbreviation: true,
    'citizen.latitude': true,
    'citizen.longitude': true,
    'citizen.sex': true,
    'citizen.ageRange': true,
    'citizenHealth.hasHypertension': true,
    'citizenHealth.hasDiabetes': true,
    'citizenHealth.hasRespiratoryDisease': true,
    'citizenHealth.hadStroke': true,
    'citizenHealth.hadHeartAttack': true,
    'citizenHealth.hadKidneyDisease': true,
    'citizenHealth.isBedridden': true,
    'citizenHealth.isPregnant': true,
    'citizenHealth.smoker': true,
  }).shape,
)
const expressionForAssertions = expression(
  immunobiologicalShape.pick({
    'citizen.sex': true,
  }).shape,
)

export const immunobiologicalVariable: Variable = {
  key: 'immunobiological',
  actionDefinitionKey: immunobiologicalStatsAction.key,
  title: 'Aplicações de vacina',
  categoryKey: 'healthImmunobiologicals',
  unitSingular: 'aplicação',
  unitPlural: 'aplicações',
  grammaticalGender: 'female',
  timeType: 'incidence',
  shape: getZodSchemaInfo(immunobiologicalShape),
  betterDensity: 'more',
  relevance: healthVariableBaseRelevance.immunobiological,
  basicSelect: ['citizen.name', 'citizen.socialName'],
  detailedSelect: [
    /* IMUNIZAÇÃO */
    'administrationDateTime',
    'immunobiologicalDescription',
    'vaccineDoseDescription',
    'vaccineBatch',
    'attendanceGroup',
    'vaccineManufacturer',
    'professionalName',
    'healthUnitName',
    'teamName',

    /* CIDADÃO */
    'citizen.sex',
    'citizen.birthDate',
    'citizen.phoneNumber',
    'citizen.cns',
    'citizen.cpf',
    'citizen.streetName',
    'citizen.streetNumber',
    'citizen.neighborhood',
    'citizen.complement',
    'citizen.referencePoint',
    'citizen.postalCode',
    'citizen.deceased',
    'citizen.latitude',
    'citizen.longitude',
    'citizen.originalId',

    /* SAÚDE */
    'citizenHealth.hasHypertension',
    'citizenHealth.hasDiabetes',
    'citizenHealth.hasRespiratoryDisease',
    'citizenHealth.hadStroke',
    'citizenHealth.hadHeartAttack',
    'citizenHealth.hadKidneyDisease',
    'citizenHealth.isBedridden',
    'citizenHealth.isPregnant',
    'citizenHealth.smoker',
    'citizenHealth.isResponsibleFamilyMember',
    'citizenHealth.mothersName',
    'citizenHealth.fathersName',
    'citizenHealth.email',
    'citizenHealth.skinColorOrRace',
    'citizenHealth.nationality',
    'citizenHealth.countryOfBirth',
    'citizenHealth.birthplaceMunicipality',
    'citizenHealth.birthplaceState',
    'citizenHealth.occupation',
    'citizenHealth.hasPrivateHealthInsurance',
    'citizenHealth.memberOfTraditionalGroupsOrCommunity',
    'citizenHealth.currentWeightStatus',
    'citizenHealth.consumesAlcoholicBeverages',
    'citizenHealth.hasAnyDisability',
    'citizenHealth.whichKidneyDisease',
    'citizenHealth.whichRespiratoryDisease',
    'citizenHealth.hasLeprosy',
    'citizenHealth.hasTuberculosis',
    'citizenHealth.hadCancer',
    'citizenHealth.hasMedicalProfessionalDiagnosisOfMentalConditions',
    'citizenHealth.receivesBenefit',
    'citizenHealth.hasPrivateHealthInsurance',
  ],
  filterableBy: getZodSchemaInfo(
    immunobiologicalShape.pick({
      immunobiologicalCode: true,
      vaccineDoseAbbreviation: true,
      'citizen.sex': true,
      'citizen.ageRange': true,
      'citizen.deceased': true,
      'citizenHealth.hasHypertension': true,
      'citizenHealth.hasDiabetes': true,
      'citizenHealth.hasRespiratoryDisease': true,
      'citizenHealth.hadStroke': true,
      'citizenHealth.hadHeartAttack': true,
      'citizenHealth.hadKidneyDisease': true,
      'citizenHealth.isBedridden': true,
      'citizenHealth.isPregnant': true,
      'citizenHealth.smoker': true,
    }),
  ),
  timeSeries: {
    model: 'additive',
    fitDays: 365 * 10,
    layers: [
      ...defaultDetectionLayers,
      {
        layer: 'calendarSeasonality',
        type: 'monthOfYear',
        degree: 1,
        optimization: {
          minimumDays: 1095,
          lossFunction: 'recentWeightedMeanSquaredError',
        },
      },
      {
        layer: 'trend',
        degree: 2,
      },
      {
        layer: 'calendarSeasonality',
        type: 'dayOfWeek',
        degree: 1,
        optimization: {
          minimumDays: 60,
          lossFunction: 'recentWeightedMeanSquaredError',
        },
      },
      {
        layer: 'calendarSeasonality',
        type: 'holiday',
        degree: 1,
      },
      {
        layer: 'calendarSeasonality',
        type: 'dayOfMonth',
        degree: 1,
      },
      {
        layer: 'harmonicSeasonality',
        optimization: {
          minimumDays: 30,
          lossFunction: 'gradientMeanSquaredError',
        },
      },
      {
        layer: 'harmonicSeasonality',
        optimization: {
          minimumDays: 30,
          lossFunction: 'gradientMeanSquaredError',
        },
      },
    ],
  },
  assertions: prepareAssertions(expressionForAssertions),
}

const immunobiologicalStatsDefinition = defineAction({
  ...immunobiologicalStatsAction,
  input: z.object({
    variable,
    highlight: expressionForCharacteristics.optional(),
    filter: expressionForCharacteristics.optional(),
    space: spaceForPoint().optional(),
    time: timeForIncidence(),
    select: select(immunobiologicalShape),
    assertions,
    analysis,
  }),
  output: z.any(),
  cache: input => {
    if (
      input.filter ||
      input.space?.delimitationType === 'custom' ||
      (input.time?.type &&
        ['customDateRange', 'customDateRangePredicted'].includes(input.time?.type))
    ) {
      return {}
    }
    return {
      back: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
      front: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
    }
  },
})

export default immunobiologicalStatsDefinition
